import { useState } from 'react'
import { Button, Form, Input, Select, DatePicker, Tag, Popconfirm, Drawer, Table, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { rfqApi, purchaseRequisitionsApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { RFQ } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'

const STATUS_COLORS: Record<string, string> = { OPEN: 'blue', CLOSED: 'cyan', CANCELLED: 'red' }

export default function RFQPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [vendorForm] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [vendorDrawer, setVendorDrawer] = useState(false)
  const [selectedRFQ, setSelectedRFQ] = useState<RFQ | null>(null)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['rfqs', plantFilter],
    queryFn: () => rfqApi.list({ plant_id: plantFilter }),
  })
  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const { data: prs } = useQuery({
    queryKey: ['prs-approved'],
    queryFn: () => purchaseRequisitionsApi.list({ status: 'APPROVED' }),
  })
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-all'],
    queryFn: () => suppliersApi.list({ limit: 500 }),
  })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const prOptions = prs?.map(p => ({ label: `${p.pr_no} — ${p.pr_type}`, value: p.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        submission_deadline: values.submission_deadline
          ? dayjs(values.submission_deadline as string).toISOString()
          : undefined,
      }
      return rfqApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rfqs'] }); message.success('RFQ created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const closeMutation = useMutation({
    mutationFn: (id: string) => rfqApi.close(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rfqs'] }); message.success('RFQ closed') },
    onError: () => message.error('Close failed'),
  })

  const addVendorMutation = useMutation({
    mutationFn: ({ rfqId, supplierId }: { rfqId: string; supplierId: string }) =>
      rfqApi.addVendor(rfqId, { supplier_id: supplierId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rfqs'] }); message.success('Vendor added'); vendorForm.resetFields() },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const openVendors = (rfq: RFQ) => { setSelectedRFQ(rfq); setVendorDrawer(true) }

  const columns = [
    { title: 'RFQ No', dataIndex: 'rfq_no', key: 'rfq_no', width: 120 },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    { title: 'Type', dataIndex: 'rfq_type', key: 'rfq_type', width: 90 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: 'Vendors', key: 'vendors', width: 80,
      render: (_: unknown, r: RFQ) => r.vendors.length,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_: unknown, r: RFQ) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Button size="small" onClick={() => openVendors(r)}>Vendors</Button>
          {r.status === 'OPEN' && (
            <Popconfirm title="Close this RFQ?" onConfirm={() => closeMutation.mutate(r.id)}>
              <Button size="small">Close</Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="RFQ"
        subtitle="Request for Quotation — invite suppliers to quote"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ rfq_type: 'GOODS' }); setDrawerOpen(true) }}>New RFQ</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New RFQ"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="rfq_type" label="RFQ Type" rules={[{ required: true }]}>
          <Select options={[{ label: 'GOODS', value: 'GOODS' }, { label: 'SERVICE', value: 'SERVICE' }]} />
        </Form.Item>
        <Form.Item name="pr_id" label="Against PR (optional)">
          <Select allowClear options={prOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="description" label="Description" rules={[{ required: true }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="submission_deadline" label="Submission Deadline">
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
      </FormDrawer>

      <Drawer
        title={`Vendors — ${selectedRFQ?.rfq_no}`}
        open={vendorDrawer}
        onClose={() => setVendorDrawer(false)}
        width={500}
      >
        {selectedRFQ && (
          <>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={selectedRFQ.vendors}
              columns={[
                { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
                { title: 'Status', dataIndex: 'response_status', key: 'response_status',
                  render: (v: string) => <Tag color={v === 'RESPONDED' ? 'green' : v === 'NO_RESPONSE' ? 'red' : 'blue'}>{v}</Tag> },
              ]}
              style={{ marginBottom: 16 }}
            />
            {selectedRFQ.status === 'OPEN' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  style={{ flex: 1 }}
                  placeholder="Add supplier"
                  options={supplierOptions}
                  showSearch optionFilterProp="label"
                  onChange={(v) => addVendorMutation.mutate({ rfqId: selectedRFQ.id, supplierId: v })}
                />
              </div>
            )}
          </>
        )}
      </Drawer>
    </div>
  )
}
